import React from "react";
import { useNavigate } from "react-router-dom";
import { useWorkspace } from "../../context/WorkspaceContext";
import { toSortedArray, renameDesk, renameDrawer, renameNotebook, updateText } from "../../services/workspaceService";
import { Map, Briefcase, Box, Layers, FileText } from "lucide-react";
import Usable from "../common/Usable";
import HomeShell, { HomePageContent } from "../shell/HomeShell";
import { BACKGROUNDS } from "../../config/backgrounds";
import { useEditLock } from "../../context/EditLockContext";
import deskGlass from "../../styles/deskGlass";

const glass = deskGlass;

export default function WorkspaceAtlas() {
  const { workspace, loadData } = useWorkspace();
  const navigate = useNavigate();
  const { canEdit } = useEditLock();
  const locked = !canEdit;

  if (!workspace) return (
    <HomeShell background={BACKGROUNDS.dashboard} fullHeight={false}>
      <div style={glass.center}>Abriendo mapas del Vault...</div>
    </HomeShell>
  );

  const handleNavigate = (desk, drawer, notebook, text) => {
    navigate(`/a/${desk.slug}/${drawer.slug}/${notebook.slug}/${text.id}`);
  };

  return (
    <HomeShell background={BACKGROUNDS.dashboard} fullHeight={false}>
    <HomePageContent wide>
    <div style={{ ...glass.deskContent, maxWidth: "1400px", paddingBottom: "120px" }}>
      <header style={{ marginBottom: "32px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "8px" }}>
          <Map size={32} color="#D4AF37" />
          <h1 style={glass.greeting}>Atlas del Vault</h1>
        </div>
        <p style={glass.subtext}>Explora la arquitectura de tus manuscritos y cajones.</p>
      </header>

      <div style={styles.atlasGrid}>
        {(workspace.desks || []).map(desk => (
          <div key={desk.id} style={glass.glassSection}>
            <Usable
              location={[desk.name]}
              title={desk.name}
              onRename={locked ? undefined : async (name) => { await renameDesk(desk.id, name); if (loadData) await loadData(); }}
              style={{ ...glass.glassListItem, marginBottom: "20px", borderBottom: "1px solid rgba(255,255,255,0.08)" }}
            >
              <Box size={18} color="#D4AF37" />
              <span style={styles.deskName}>{desk.name}</span>
            </Usable>
            
            <div style={styles.drawerList}>
              {toSortedArray(desk.drawers).map(drawer => (
                <div key={drawer.id} style={styles.drawerSection}>
                  <Usable
                    location={[desk.name, drawer.name]}
                    title={drawer.name}
                    onRename={locked ? undefined : async (name) => { await renameDrawer(desk.id, drawer.id, name); if (loadData) await loadData(); }}
                    style={styles.drawerHeader}
                  >
                    <Briefcase size={16} color="#886B4A" />
                    <span style={styles.drawerName}>{drawer.name}</span>
                  </Usable>

                  <div style={styles.notebookGrid}>
                    {toSortedArray(drawer.notebooks).map(notebook => (
                      <div key={notebook.id} style={{ ...glass.glassCard, padding: "10px", cursor: "default" }}>
                        <div style={{ width: "100%", height: "4px", background: notebook.color || "#D4AF37", borderRadius: "2px", marginBottom: "8px" }} />
                        <div style={styles.notebookContent}>
                          <Usable
                            location={[desk.name, drawer.name, notebook.name]}
                            title={notebook.name}
                            onRename={locked ? undefined : async (name) => { await renameNotebook(desk.id, drawer.id, notebook.id, name); if (loadData) await loadData(); }}
                            style={{ marginBottom: "8px" }}
                          >
                            <div style={styles.notebookTitle}>
                              <Layers size={14} style={{ opacity: 0.6 }} />
                              {notebook.name}
                            </div>
                          </Usable>
                          
                          <div style={styles.articleList}>
                            {toSortedArray(notebook.texts).slice(0, 5).map(text => (
                              <Usable
                                key={text.id}
                                location={[desk.name, drawer.name, notebook.name, text.title]}
                                title={text.title}
                                onRename={locked ? undefined : async (name) => { await updateText(desk.id, drawer.id, notebook.id, text.id, { title: name }); if (loadData) await loadData(); }}
                                copyLabel="Copiar título"
                                copyValue={text.title}
                                onClick={() => handleNavigate(desk, drawer, notebook, text)}
                                style={{ ...glass.glassRow, padding: "6px 10px", fontSize: "0.75rem", cursor: "pointer" }}
                              >
                                <FileText size={12} />
                                <span style={styles.articleTitle}>{text.title}</span>
                              </Usable>
                            ))}
                            {toSortedArray(notebook.texts).length > 5 && (
                              <div style={styles.moreCount}>
                                ...y {toSortedArray(notebook.texts).length - 5} más
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
    </HomePageContent>
    </HomeShell>
  );
}

const styles = {
  container: {
    padding: "40px 20px",
    maxWidth: "1400px",
    margin: "0 auto",
    fontFamily: "'Playfair Display', serif",
  },
  header: {
    marginBottom: "40px",
    borderBottom: "1px solid rgba(212,175,55,0.1)",
    paddingBottom: "20px",
  },
  titleGroup: { display: "flex", alignItems: "center", gap: "16px", marginBottom: "8px" },
  title: { fontSize: "2.2rem", color: "#D4AF37", margin: 0, fontWeight: "normal" },
  subtitle: { color: "#888", fontSize: "1.1rem", fontStyle: "italic" },
  atlasGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(400px, 1fr))",
    gap: "30px",
  },
  deskCard: {
    background: "rgba(26, 16, 12, 0.45)",
    backdropFilter: "blur(12px)",
    borderRadius: "12px",
    border: "1px solid rgba(212,175,55,0.15)",
    padding: "24px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
  },
  deskHeader: { display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px", borderBottom: "1px solid rgba(212,175,55,0.1)", paddingBottom: "12px" },
  deskName: { color: "#D4AF37", fontSize: "1.2rem", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "1px" },
  drawerList: { display: "flex", flexDirection: "column", gap: "24px" },
  drawerSection: { paddingLeft: "12px", borderLeft: "2px solid rgba(136, 107, 74, 0.2)" },
  drawerHeader: { display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" },
  drawerName: { color: "#886B4A", fontSize: "0.95rem", fontWeight: "bold" },
  notebookGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: "12px" },
  notebookItem: {
    background: "rgba(255,255,255,0.03)",
    borderRadius: "8px",
    overflow: "hidden",
    border: "1px solid rgba(255,255,255,0.05)",
    display: "flex",
    flexDirection: "column",
  },
  notebookTag: { height: "4px", width: "100%" },
  notebookContent: { padding: "10px" },
  notebookTitle: { fontSize: "0.85rem", fontWeight: "bold", color: "#D4AF37", marginBottom: "8px", display: "flex", alignItems: "center", gap: "6px" },
  articleList: { display: "flex", flexDirection: "column", gap: "4px" },
  articleLink: { 
    display: "flex", 
    alignItems: "center", 
    gap: "6px", 
    color: "#aaa", 
    fontSize: "0.75rem", 
    cursor: "pointer", 
    transition: "color 0.2s" 
  },
  articleTitle: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  moreCount: { fontSize: "0.65rem", color: "#666", marginTop: "2px", fontStyle: "italic" },
  center: { display: "flex", justifyContent: "center", alignItems: "center", height: "60vh", color: "#D4AF37", fontSize: "1.2rem" },
};
